import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { OrderUpdateAction } from 'src/app/model/interfaces/order-update-action';
import { NavBarService } from 'src/app/services';
import { PriceService } from 'src/app/services/price.service';
import { Order } from '../../../model/interfaces/order';
import { OrderService } from '../../../services/order.service';
import { PaymentInterval } from '../../../model/types/payment-interval';

@Component({
  selector: 'app-basket-shell-purchase',
  templateUrl: './basket-shell-purchase.component.html',
  styleUrls: ['./basket-shell-purchase.component.scss'],
})
export class BasketShellPurchaseComponent implements OnInit, OnDestroy {
  private readonly orderService: OrderService;
  private readonly navBarService: NavBarService;
  private orderUpdatedSubscription: Subscription;
  private onOrderUpdatedSubscription = new Subscription();
  private navbarBasketIconClickSubscription: Subscription;
  private orderExpiresNotificationSubscription: Subscription;
  private orderExpiredSubscription: Subscription;
  private orderUpdatePendingSubscription: Subscription;
  private orderFullSubscription: Subscription;
  private pendingUpdateActiveHandle = null;
  private changeDetector: ChangeDetectorRef;
  private updatePendingSince = new Date();
  private router: Router;
  private readonly priceService: PriceService;

  public order: Order;
  public notifyDelete: boolean;
  public expanded: boolean;
  public itemCount = 0;
  public pendingUpdateActive = false;
  public pendingUpdates = 0;
  public totalPrice: any;
  public totalFees: any;
  public classCount: PaymentInterval;

  @Input('navigate-checkout')
  public navigateCheckout = true;

  @Input('is-checkout')
  public isCheckout = false;

  @Input('toast')
  public toast = true;

  @Input() showFees: boolean = false;

  @Input('show-per-month')
  public showPerMonth = false;

  @ViewChild('content', { static: true })
  public content: any;

  public constructor(
    changeDetector: ChangeDetectorRef,
    navBarService: NavBarService,
    orderService: OrderService,
    priceService: PriceService,
    router: Router
  ) {
    this.changeDetector = changeDetector;
    this.orderService = orderService;
    this.navBarService = navBarService;
    this.priceService = priceService;
    this.router = router;
  }

  public async ngOnInit(): Promise<void> {
    this.setUpSubscriptions();
    const order = await this.orderService.getOrder(false);
    this.setItemCount(order);
    this.update();
  }

  private setUpSubscriptions(): void {
    this.onOrderUpdatedSubscription = this.orderService.onOrderContentUpdated.subscribe(() =>
      this.onOrderContentUpdated()
    );
    this.orderUpdatedSubscription = this.orderService.onOrderContentUpdated.subscribe(
      (updateInfo: { order: Order; action: OrderUpdateAction }) => {
        this.orderUpdated(updateInfo.order, updateInfo.action);
      }
    );

    this.navbarBasketIconClickSubscription = this.navBarService.menuClick.subscribe(
      (menuItem: { name: string; route: string }) => {
        if (menuItem.name === 'basket') {
          this.toggleExpanded();
        }
      }
    );

    if (this.toast) {
      if (this.navBarService.showBasket) {
        this.toggleExpanded();
        this.navBarService.showBasketFlag(false);
      }
    }

    this.orderExpiresNotificationSubscription = this.orderService.onOrderExpirationNotice.subscribe(
      (order: Order) => {
        // TODO: Should this trigger a notification to the user in a modal.
        console.warn('Order expires in a short while');
      }
    );

    this.orderExpiredSubscription = this.orderService.onOrderExpired.subscribe((order: Order) => {
      this.terminate();
    });

    this.orderUpdatePendingSubscription = this.orderService.onOrderUpdateIntent.subscribe(() =>
      this.setUpdatePending()
    );
    this.orderFullSubscription = this.orderService.onOrderFull.subscribe(() => this.orderFull());
  }

  private onOrderContentUpdated(): void {
    this.update();
  }

  private async update(): Promise<void> {
    const order = await this.getOrder();
    if (order != null) {
      this.order = order;
      this.getTotalPrice();
    }
  }

  private getTotalPrice(): void {
    this.priceService.getPriceByOrder(this.order).subscribe((res) => {
      this.totalPrice = res.totalPrice;
      this.totalFees = res.fees?.map((a) => a.value).reduce((a, b) => a + b, 0);
    });
    this.orderService.getOrder(false).then((order) => {
      this.classCount = order.NumberOfClasses;
    });
  }

  private async getOrder(): Promise<Order> {
    return this.orderService.getOrder(false);
  }

  private setUpdatePending(): void {
    window.clearTimeout(this.pendingUpdateActiveHandle);
    this.pendingUpdateActive = true;

    setTimeout(() => {
      this.updatePendingSince = new Date();
      this.pendingUpdates++;
      this.changeDetector.detectChanges();
    }, 0);
  }

  private orderUpdated(order: Order, action: OrderUpdateAction): void {
    const actionType = action ? action.actionType : 'unknown';

    // Filter that makes sure we only process the event when something is deleted from or added to the order.
    if (!(actionType === 'add' || actionType === 'delete')) {
      return;
    }

    this.setItemCount(order);

    if (this.itemCount > 0) {
      this.setItemCount(order);

      if (action.actionType === 'delete') {
        this.itemDeleted();
      }
    } else if (this.itemCount === 0) {
      this.expanded = false;
    }

    this.decrementPendingUpdate();
  }

  private orderFull(): void {
    this.decrementPendingUpdate();
  }

  private decrementPendingUpdate(): void {
    const delay = Math.max(0, 1000 - (+new Date() - +this.updatePendingSince));

    setTimeout(() => {
      // Safe check to avoid negative values caused by unforeseen events.
      this.pendingUpdates = Math.max(0, this.pendingUpdates - 1);
      this.pendingUpdateActiveHandle = setTimeout(() => {
        this.pendingUpdateActive = false;
        this.changeDetector.detectChanges();
      }, 1000);
    }, delay);
  }

  private itemDeleted(): void {
    this.notifyDelete = true;
    setTimeout(() => {
      this.notifyDelete = false;
    }, 1000);
  }

  public toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  public onShellClick(event: MouseEvent): void {
    this.expanded = false;
  }

  private terminate(): void {
    if (this.expanded) {
      this.expanded = false;
    }

    this.setItemCount(null);
  }

  private setItemCount(order: Order | null): void {
    const itemCount = order != null ? order.Shares.length + order.TicketPackageInstances.length : 0;
    this.itemCount = itemCount;
  }

  public goToPage(url: string): void {
    this.router.navigateByUrl(`/lodder-til-salg${url}`);
  }

  public ngOnDestroy(): void {
    clearTimeout(this.pendingUpdateActiveHandle);
    this.orderUpdatedSubscription.unsubscribe();
    this.navbarBasketIconClickSubscription.unsubscribe();
    this.orderExpiresNotificationSubscription.unsubscribe();
    this.orderExpiredSubscription.unsubscribe();
    this.orderUpdatePendingSubscription.unsubscribe();
    this.orderFullSubscription.unsubscribe();
  }
}
